import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import { classMaterialsPath } from '../../../config/contentRoutes';

interface ClassMaterialItem {
  id: string;
  title: string;
  category: string;
  subject: string;
  description: string;
  author: string;
  linkPath?: string;
}

const categoryLabels: Record<string, string> = {
  'lesson-plan': '수업 계획서',
  'project-guide': '프로젝트 가이드',
  assessment: '평가 자료',
  worksheet: '학습 활동지',
  presentation: '발표 자료',
};

const ClassMaterialsManage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const authorName = currentUser?.name || currentUser?.email || '교사';

  const [materials, setMaterials] = useState<ClassMaterialItem[]>([
    {
      id: 'ai-robot-project',
      title: 'AI 로봇 제작 프로젝트',
      category: 'project-guide',
      subject: 'technology',
      description: 'Micro:bit를 활용한 AI 로봇 제작 프로젝트 학습 자료입니다.',
      author: authorName,
      linkPath: classMaterialsPath('ai-robot-project'),
    },
  ]);
  const [search, setSearch] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');

  const filtered = useMemo(
    () =>
      materials.filter(
        (item) =>
          item.title.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase())
      ),
    [materials, search]
  );

  const handleAdd = () => {
    if (!draftTitle.trim()) {
      window.alert('자료 제목을 입력해 주세요.');
      return;
    }

    const id = `material-${Date.now()}`;
    setMaterials((prev) => [
      ...prev,
      {
        id,
        title: draftTitle.trim(),
        category: 'lesson-plan',
        subject: 'technology',
        description: draftDescription.trim() || '설명 없음',
        author: authorName,
      },
    ]);
    setDraftTitle('');
    setDraftDescription('');
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('이 자료를 내 목록에서 삭제할까요?')) return;
    setMaterials((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
              내 수업 자료 관리
            </h1>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              내가 등록한 수업 자료만 편집합니다. 다른 사람에게 보이는 공개 페이지와 주소가 다릅니다.
            </p>
          </div>
          <Button variant="outline" size="medium" onClick={() => navigate(classMaterialsPath('materials'))}>
            공개 페이지 보기
          </Button>
        </div>

        <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)">
          <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
            자료 추가
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <InputField value={draftTitle} onChange={setDraftTitle} label="제목" placeholder="자료 제목" size="medium" />
            <InputField
              value={draftDescription}
              onChange={setDraftDescription}
              label="설명"
              placeholder="간단한 설명"
              size="medium"
            />
            <div>
              <Button variant="primary" size="medium" onClick={handleAdd}>
                내 목록에 추가
              </Button>
            </div>
          </div>
        </Card>

        <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              내 자료 ({filtered.length})
            </h2>
            <InputField value={search} onChange={setSearch} placeholder="검색..." size="small" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filtered.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--Color-text-normal)', fontSize: '14px' }}>등록한 자료가 없습니다.</p>
            ) : (
              filtered.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid var(--Color-line-normal)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)', marginBottom: '4px' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--Color-text-normal)', marginBottom: '4px' }}>
                      {categoryLabels[item.category] ?? item.category} · {item.author}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>{item.description}</div>
                    {item.linkPath && (
                      <button
                        type="button"
                        onClick={() => navigate(item.linkPath!)}
                        style={{
                          marginTop: '8px',
                          padding: 0,
                          border: 'none',
                          background: 'none',
                          color: 'var(--Color-fill-brand-color)',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        공개 페이지 미리보기 →
                      </button>
                    )}
                  </div>
                  <Button variant="cancel" size="small" onClick={() => handleDelete(item.id)}>
                    삭제
                  </Button>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClassMaterialsManage;
